import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { countryConfig } from '../../country';
import { WatchButton } from '../../components/watch-button';
import { BuyerTrustStrip } from '../../components/buyer-trust-strip';
import { GoodStandingBadge } from '../../components/good-standing-badge';
import { MobileSectionNav } from '../../components/mobile-section-nav';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { isLinkableTin, ORG_ID_PROPERTY } from '../../lib/tin';
import { Seo } from '../../components/seo';
import { localePath } from '../../i18n/locale-path';
import { termsPath } from '../../content/legal/es/paths';
import { datasetPlaceName } from '../../lib/country-name-en';
import { StatTile } from '../../components/charts/stat-tile';
import { SettlementTile } from '../../components/settlement-tile';
import { RankedBars } from '../../components/charts/ranked-bars';
import { useLocale, useT } from '../../i18n/locale-context';
import { useAuth } from '../../lib/auth-context';
import { usePageTitle } from '../../lib/page-title';
import { tenderPath } from '../../lib/slug';
import { isNameableRow } from '../../lib/recent-rows';
import { track } from '../../lib/funnel';
import {
  getBuyerProfile,
  getPublicBuyerProfile,
  MIN_BUYER_AWARDS,
  type BuyerProfile,
  type PublicBuyerProfile,
} from '../../lib/catalog-client';
import {
  amountShown,
  formatBudgetCompact,
  formatDeadline,
  pctStat,
  withCurrency,
} from '../../lib/format';
import { displaySourceText } from '../../lib/source-text';
import {
  concentrationPillLevel,
  concentrationRead,
  hhiTitle,
  concentrationSentence,
  mayAssertConcentration,
} from '../../lib/concentration-read';
import { CONCENTRATION_PILL_CLASS } from '../../components/analytics-card-parts';
import { InfoHint } from '../../components/info-hint';
import { localizeRegion } from '../../lib/regions';
import { shortenOrgName } from '../../lib/org-name';
import { extractOrgTin } from '@tenders/shared';

const KICKER = 'text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft';

/** Pro-gated page for a government buyer (заказчик, by ИНН): scale, supplier
 *  concentration + favoritism (plain-words verdict, top-supplier value/count share, repeat-winner
 *  streak), annual award trend, category mix and recent awards. #48. */
export function BuyerProfile() {
  // The route param may be slugged (`{tin}-{name}`, INN-first) or the bare ИНН —
  // recover the ИНН, which is what every API call and query key keys on. Old
  // bare-ИНН URLs (already indexed) keep resolving; the slugged form is canonical.
  // Spain's NIF starts with a letter (`Q3018001B-…`); the country code lets the
  // extractor match it — UZ/KZ keep the leading-digits rule.
  const { tin: tinParam } = useParams<{ tin: string }>();
  const tin = tinParam ? extractOrgTin(tinParam, countryConfig.code) : undefined;
  const navigate = useNavigate();
  const { locale } = useLocale();
  const t = useT();
  const { user } = useAuth();
  const authed = user != null;
  usePageTitle(t('buyer.title'));

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.querySelector('main')?.scrollTo({ top: 0 });
  }, [tin]);

  // Pro value-realization — an authed user opened the buyer intelligence page.
  useEffect(() => {
    if (authed) track('buyer_profile_viewed', { tin });
  }, [tin, authed]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['buyer-profile', tin, locale],
    queryFn: () => getBuyerProfile(tin!, locale),
    enabled: authed && !!tin,
  });

  // Anonymous tier — ALWAYS enabled, and server-seeded by the prerender. This
  // is what turns the page from a noindex paywall stub into ~4.2k indexable
  // buyer pages; the Pro query above still owns the counterparty graph.
  const publicQuery = useQuery({
    queryKey: ['buyer-public', tin, locale],
    queryFn: () => getPublicBuyerProfile(tin!, locale),
    enabled: !!tin,
    staleTime: 10 * 60 * 1000,
  });
  const pub = publicQuery.data?.profile ?? null;

  const locked = !authed || !data || data.locked;
  const profile = !locked && !data.locked ? data.profile : null;

  // Same gap as the supplier page: a Pro wall that was in the vocabulary but
  // never emitted, so it looked dead rather than uninstrumented.
  useEffect(() => {
    if (locked) track('paywall_shown', { wall: 'buyerProfile', tin });
  }, [locked, tin]);

  // Index only what is worth indexing, and the SERVER decides — see the supplier
  // page for the full note. The bar is `awards >= 2` counted over
  // `tender_results` (company-index-rule.ts); `totalAwards` below is a DIFFERENT
  // number (it unions bid_history), which is precisely why this is no longer
  // derived locally. The fallback covers the API-one-deploy-behind window only.
  //
  // (`MIN_BUYER_AWARDS` no longer gates the concentration block either — since
  // 2026-10-07 the figures render from 1 contract and only the verdict's
  // strength scales with the count; see `lib/concentration-read`.)
  const indexable = pub != null && (pub.indexable ?? pub.totalAwards >= MIN_BUYER_AWARDS);
  const seoName = pub?.name ?? null;
  // Distinctive name for the visible <title>/<h1> — the registered legal form
  // («Общество с ограниченной ответственностью …» / «… MAS'ULIYATI CHEKLANGAN
  // JAMIYAT») otherwise eats the SERP snippet before the buyer's actual name is
  // reached. The FULL name is kept for JSON-LD `name` (entity matching) below.
  const seoShort = seoName ? shortenOrgName(seoName).short : null;
  // The facts the title/description quote. `formatBudgetCompact` is manual (not
  // `Intl.NumberFormat` compact), so it does not hit the missing-`uz`-CLDR trap
  // that renders «1.9G» in Chrome while passing every Node test.
  const seoParams = pub
    ? {
        name: seoShort ?? pub.tin,
        tin: pub.tin,
        awards: pub.totalAwards,
        suppliers: pub.distinctSuppliers,
        active: pub.activeTenders,
        value: withCurrency(
          formatBudgetCompact(String(Math.round(pub.totalValueUzs)), locale) ?? '0',
          t('tender.budget.unit'),
        ),
      }
    : null;

  // Absolute, locale-correct URL for a bare in-app path (for canonical/JSON-LD).
  // Canonical/JSON-LD point at the slugged URL (INN-first) when we have it — the
  // sitemap advertises the same slug — falling back to the bare ИНН before load.
  const buyerPath = `/buyer/${pub?.slug ?? tin}`;
  const absUrl = (p: string) => `${countryConfig.brand.siteUrl}${localePath(p, locale)}`;

  // Structured data, emitted ONLY for indexable buyers (same bar as the meta
  // robots gate): a thin one-award buyer has no schema, matching the tender
  // page's discipline. A government заказчик is a GovernmentOrganization; the
  // award record it anchors is declared free/public, and a breadcrumb clarifies
  // site structure the same way the tender page does.
  const jsonLd =
    indexable && pub && seoName
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'GovernmentOrganization',
              name: seoName,
              // The ИНН is the buyer's stable public identifier — the join key
              // across every award and the thing that disambiguates two
              // like-named органов.
              identifier: { '@type': 'PropertyValue', propertyID: ORG_ID_PROPERTY, value: tin },
              url: absUrl(buyerPath),
              ...(pub.region
                ? { areaServed: { '@type': 'AdministrativeArea', name: pub.region } }
                : {}),
              // The aggregate this page is actually about: how much this buyer
              // has awarded and to how many distinct suppliers. Public by design.
              subjectOf: {
                '@type': 'Dataset',
                name: seoParams ? t('buyer.seoTitle', seoParams) : seoName,
                // GSC 2026-09-07: 161 of 165 Dataset objects invalid for a missing
                // `description`, every one warned for `license`. Same fields the
                // /prices Dataset already carries; `Place` (not `Country`) for
                // spatialCoverage — Country was flagged as an invalid type.
                description: seoParams ? t('buyer.seoDescription', seoParams) : seoName,
                url: absUrl(buyerPath),
                license: absUrl(termsPath(countryConfig.code)),
                spatialCoverage: { '@type': 'Place', name: datasetPlaceName(countryConfig.code) },
                variableMeasured: [t('buyer.kpi.awards'), t('buyer.kpi.value')],
                isAccessibleForFree: true,
                inLanguage: locale,
                creator: {
                  '@type': 'Organization',
                  name: countryConfig.brand.name,
                  url: `${countryConfig.brand.siteUrl}/`,
                },
              },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: t('nav.catalog'), item: absUrl('/') },
                { '@type': 'ListItem', position: 2, name: seoName, item: absUrl(buyerPath) },
              ],
            },
          ],
        }
      : undefined;

  return (
    <>
      <Seo
        title={seoParams ? t('buyer.seoTitle', seoParams) : t('buyer.title')}
        description={seoParams ? t('buyer.seoDescription', seoParams) : t('buyer.title')}
        // Self-canonical + hreflang cluster (uz/ru/x-default), previously absent
        // on this template while every tender page had them. The `canonical` prop
        // is what unlocks both in <Seo>. Gated on `indexable` so a thin/noindex
        // buyer stub keeps its exact prior output (no canonical, no alternates).
        canonical={indexable ? buyerPath : undefined}
        lang={locale}
        jsonLd={jsonLd}
        noIndex={!indexable}
      />
      <div className="mx-auto max-w-3xl lg:max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-[13px] font-semibold text-ink-muted hover:text-brand"
        >
          {t('buyer.back')}
        </button>

        {authed && isLoading ? (
          <div className="h-40 animate-pulse rounded-lg border border-edge bg-bone-surface shadow-m-sm" />
        ) : locked && pub ? (
          // Not entitled, but the aggregates are public — so the page carries
          // real content instead of a bare wall, and the upsell sits BELOW it
          // rather than in place of it.
          <>
            <PublicBuyerBody pub={pub} locale={locale} t={t} />
            <section className="mt-5 rounded-lg border border-edge bg-bone-surface p-6 text-center shadow-m-sm">
              <p className="mb-4 text-[14px] text-ink-muted">{t('buyer.locked')}</p>
              <Link
                to={authed ? '/plans' : '/register'}
                className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-6 text-[14px] font-bold text-white hover:bg-brand-mid sm:w-auto"
              >
                {authed ? t('buyer.lockedCta') : t('register.createAccount')}
              </Link>
            </section>
          </>
        ) : locked && publicQuery.isLoading ? (
          <div className="h-40 animate-pulse rounded-lg border border-edge bg-bone-surface shadow-m-sm" />
        ) : locked && publicQuery.isError ? (
          // The public fetch FAILED, so we do not know whether this buyer has
          // data. Claiming "no data" here would be a lie told by an outage —
          // fall back to the Pro teaser, which is what shipped before A2.
          <section className="rounded-lg border border-edge bg-bone-surface p-6 text-center shadow-m-sm">
            <h1 className="mb-1 text-[18px] font-bold text-ink">{t('buyer.title')}</h1>
            <p className="mb-4 text-[14px] text-ink-muted">{t('buyer.locked')}</p>
            <Link
              to={authed ? '/plans' : '/register'}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-6 text-[14px] font-bold text-white hover:bg-brand-mid sm:w-auto"
            >
              {authed ? t('buyer.lockedCta') : t('register.createAccount')}
            </Link>
          </section>
        ) : locked ? (
          // Public fetch SUCCEEDED and returned nothing — there is genuinely no
          // data here, and nothing behind the wall either, so an upsell would
          // be selling an empty room.
          <section className="rounded-lg border border-edge bg-bone-surface p-6 text-center shadow-m-sm">
            <h1 className="mb-1 text-[18px] font-bold text-ink">{t('buyer.title')}</h1>
            <p className="text-[14px] text-ink-muted">{t('buyer.empty')}</p>
          </section>
        ) : isError || !profile ? (
          <section className="rounded-lg border border-edge bg-bone-surface p-6 shadow-m-sm">
            <h1 className="mb-1 text-[18px] font-bold text-ink">{t('buyer.title')}</h1>
            <p className="text-[14px] text-ink-muted">{t('buyer.empty')}</p>
          </section>
        ) : (
          <ProfileBody profile={profile} locale={locale} t={t} />
        )}
      </div>
    </>
  );
}

function ProfileBody({
  profile: p,
  locale,
  t,
}: {
  profile: BuyerProfile;
  locale: string;
  t: (k: string, params?: Record<string, string | number>) => string;
}) {
  const unit = t('tender.budget.unit');
  const compact = (n: number): string => formatBudgetCompact(String(Math.round(n)), locale) ?? '0';
  const { short: shortName } = shortenOrgName(p.name);
  const c = p.concentration;
  // Facts render from the first contract (Igor, 2026-10-07: «Не надо скрывать,
  // просто не надо кричать»). Only the verdict's strength scales with the count.
  // The concentration is computed over TENDERS with an identified winner, so the
  // floor counts THOSE — a buyer with 12 awards of which 4 name a winner has a
  // 4-contract concentration, whatever the headline says. Since 2026-10-10 the
  // server also counts direct contracts (no competition) into
  // `unattributedAwards`, so they drop out of this number too. An API one
  // deploy behind sends no `unattributedAwards`; fall back to the headline count.
  const concentrationContracts = p.totalAwards - (p.unattributedAwards ?? 0);
  // No tender with a named winner → no block, as before for a buyer whose
  // winners were all unidentified. `c.distinctSuppliers` alone cannot gate it
  // any more: it is the headline count and includes direct-contract
  // counterparties.
  const hasFavoritism = concentrationContracts >= 1 && c.distinctSuppliers >= 1;
  // 1–4 contracts → «судить рано» (grey), 5–9 → «Похоже, …» and 10+ → the
  // verdict, both in the band's colour; the index only in the pill's `title`.
  const read = concentrationRead(c.hhi, concentrationContracts);
  const sentence = concentrationSentence(t, read);
  const pillLevel = concentrationPillLevel(read);
  const top = p.topWinners[0] ?? null;

  // Mobile section tabs (anchor-jump; every section stays in the DOM, like the
  // tender-detail nav). Only sections that actually render are listed.
  const navSections = [
    hasFavoritism && { id: 'buyer-favoritism', label: t('buyer.favoritism') },
    p.categoryMix.length > 0 && { id: 'buyer-categories', label: t('buyer.categories') },
    p.recentAwards.length > 0 && { id: 'buyer-recent', label: t('buyer.recentAwards') },
  ].filter(Boolean) as { id: string; label: string }[];

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <section className="rounded-lg bg-brand p-5 shadow-m-sm sm:p-6">
        <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-white/60">
          {t('buyer.title')}
        </div>
        <h1 className="text-[20px] font-extrabold leading-tight text-white [overflow-wrap:anywhere] sm:text-[24px]">
          {shortName || p.name || p.tin}
        </h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-white/55">
          <span className="tabular-nums">{t('org.tinNumber', { number: p.tin })}</span>
          {p.region && <span>· {localizeRegion(p.region, locale)}</span>}
        </div>
        {/* Composite trust badge — `onBrand` so the light-surface tier tokens
            don't collapse against the teal header. `unknown` renders nothing. */}
        <GoodStandingBadge goodStanding={p.goodStanding} kind="buyer" onBrand className="mt-2.5" />
        {/* Follow only. «Это моя компания?» (the claim flow) used to sit beside
            it, but our users are SUPPLIERS — nobody reading a buyer profile is
            that buyer, and two unexplained buttons next to each other read as
            one feature (Igor, 2026-09-06). The claim CTA stays on /supplier. */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <WatchButton tin={p.tin} kind="buyer" label={p.name ?? null} compact />
        </div>
        {hasFavoritism &&
          c.singleSupplierDependence &&
          mayAssertConcentration(concentrationContracts) && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-pill border border-white/25 bg-white/10 px-2.5 py-1 text-[12px] font-bold text-white">
              ⚠ {t('buyer.dependenceFlag', { pct: c.topSupplierShareByValuePct })}
            </p>
          )}
        {/* Scale folded into the header — contracts / value / suppliers / active
            — so the buyer's size reads in the hero, not in a detached grid below
            (which this replaces). */}
        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/15 pt-4 sm:grid-cols-4">
          <HeroKpi value={String(p.totalAwards)} label={t('buyer.kpi.awards')} />
          <HeroKpi
            value={withCurrency(compact(p.totalValueUzs), unit)}
            label={t('buyer.kpi.value')}
          />
          <HeroKpi value={String(c.distinctSuppliers)} label={t('buyer.kpi.suppliers')} />
          <HeroKpi value={String(p.activeTenders)} label={t('buyer.kpi.active')} />
        </div>
      </section>

      {/* Soliq registry trust — «О заказчике». The Pro payload carries the FULL
          field set (regDate + statusGroup), so the strip is richer than the
          public body's. `empty:hidden` drops it when no field resolves. */}
      <div className="empty:hidden">
        <BuyerTrustStrip
          status={p.status}
          vatPayer={p.vatPayer}
          charterFundUzs={p.charterFundUzs}
          statusGroup={p.statusGroup}
          regDate={p.regDate}
          liquidationDate={p.liquidationDate}
          dxmapRateType={p.dxmapRateType}
          dxmapSector={p.dxmapSector}
          kgdFlags={p.kgdFlags}
        />
      </div>

      {/* How far this buyer's contracts settle against the winning offer.
          Renders nothing below the sample floor. Pro-gated block on purpose —
          the public teaser above never receives `settlement`. */}
      <div className="mt-3">
        <SettlementTile settlement={p.settlement} />
      </div>

      {/* Mobile section tabs — smooth-scroll to the analytics blocks below. */}
      <MobileSectionNav sections={navSections} />

      {/* Concentration / favoritism */}
      {hasFavoritism && (
        <section
          id="buyer-favoritism"
          className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        >
          <div className={`mb-3 flex items-center gap-1.5 ${KICKER}`}>
            {t('buyer.favoritism')}
            {/* Same «?» as the lot card's panel — what the block reads, and that
                direct contracts are not in it. */}
            <InfoHint text={t('supplierGraph.hint')} />
          </div>
          <div
            className={`flex flex-wrap items-center gap-2 ${read.kind === 'verdict' ? 'mb-1.5' : 'mb-4'}`}
          >
            {pillLevel && (
              <span
                title={hhiTitle(t, c.hhi)}
                className={`inline-flex items-center rounded-pill border px-2.5 py-1 text-[12px] font-bold ${
                  // 5–9 and 10+ share the band's colour (Igor, 2026-10-10).
                  CONCENTRATION_PILL_CLASS[pillLevel]
                }`}
              >
                {read.kind === 'verdict'
                  ? t(`supplierGraph.concentration.${read.level}`)
                  : sentence}
              </span>
            )}
            {read.kind === 'early' && (
              <span className="text-[13px] font-semibold text-ink-soft">{sentence}</span>
            )}
            {top && (
              <span className="text-[13px] text-ink-muted">
                {t('buyer.topShareSplit', {
                  value: c.topSupplierShareByValuePct,
                  // The top supplier is chosen by VALUE, so it can win a few
                  // huge contracts — a count share below 0.05% rounds to 0.0 and
                  // reads as a contradiction ("3.8% of money, 0% of contracts").
                  // A top supplier always won ≥1, so show "<0.1" instead of "0".
                  count: c.topSupplierShareByCountPct > 0 ? c.topSupplierShareByCountPct : '<0.1',
                })}
              </span>
            )}
          </div>
          {read.kind === 'verdict' && (
            <p className="mb-4 text-[13px] text-ink-soft">
              {t(`supplierGraph.concentrationHint.${read.level}`)}
            </p>
          )}
          {top && p.topWinnerStreak >= 2 && (
            <p className="mb-4 rounded-md border border-edge bg-bone px-3 py-2 text-[13px] text-ink-soft">
              {t('buyer.streak', { n: p.topWinnerStreak, name: top.name ?? top.tin })}
            </p>
          )}
          <RankedBars
            items={p.topWinners.map((w) => ({
              label: w.name ?? w.tin,
              // Same rule as the lot card's concentration panel: a supplier
              // with a profile page (9-digit ИНН) is a link to it.
              href: isLinkableTin(w.tin) ? `/supplier/${w.tin}` : undefined,
              value: w.valueUzs,
              valueText: withCurrency(compact(w.valueUzs), unit),
              sub: t('buyer.winnerSub', {
                wins: w.wins,
                value: w.valueSharePct,
                count: w.countSharePct,
              }),
            }))}
          />
        </section>
      )}

      {/* Category mix */}
      {p.categoryMix.length > 0 && (
        <section
          id="buyer-categories"
          className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        >
          <div className={`mb-3 ${KICKER}`}>{t('buyer.categories')}</div>
          <RankedBars
            items={p.categoryMix.map((cat) => ({
              label: cat.name ?? cat.section,
              value: cat.valueUzs,
              valueText: withCurrency(compact(cat.valueUzs), unit),
              sub: t('market.count', { count: cat.awards }),
            }))}
          />
        </section>
      )}

      {/* Recent awards */}
      {p.recentAwards.length > 0 && (
        <section
          id="buyer-recent"
          className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        >
          <div className={`mb-3 ${KICKER}`}>{t('buyer.recentAwards')}</div>
          <ul className="divide-y divide-edge-light">
            {p.recentAwards.map((a, i) => (
              <li key={a.tenderId ?? i} className="py-2.5 first:pt-0">
                <div className="flex items-baseline justify-between gap-3">
                  {a.tenderId ? (
                    <Link
                      to={`/tender/${a.tenderId}`}
                      className="min-w-0 truncate text-[14px] font-semibold text-brand hover:underline"
                      title={a.title ?? a.tenderId}
                    >
                      {a.title ?? a.tenderId}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate text-[14px] font-semibold text-ink">
                      {a.title ?? '—'}
                    </span>
                  )}
                  {/* Spain: an award under 1 € is a placeholder the buyer typed —
                      «Sin importe publicado», never «0 €» (`amountShown`). */}
                  {amountShown(a.valueUzs) ? (
                    <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">
                      {withCurrency(compact(a.valueUzs), unit)}
                    </span>
                  ) : (
                    <span className="shrink-0 text-[12.5px] text-ink-muted">
                      {t('buyer.amountUndisclosed')}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-ink-muted">
                  {a.winnerName && <span className="truncate">{a.winnerName}</span>}
                  {a.awardedAt && <span>· {formatDeadline(a.awardedAt, locale) ?? ''}</span>}
                </div>
                {/* No winner phone/email here, by owner decision 2026-10-07:
                    winner contacts live on the lot outcome block and the
                    `/supplier/:tin` header only. */}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Anonymous tier of the buyer page: scale, activity and category mix — every
 * one an aggregate. No named supplier, no HHI, no favoritism streak; those are
 * the paid product and live in {@link ProfileBody}.
 *
 * This is the half that gets crawled, so it must render with no session and no
 * entitlement. See `services/api/src/modules/catalog/public-profile.ts` for
 * where the line sits and why it sits there.
 */
function PublicBuyerBody({
  pub,
  locale,
  t,
}: {
  pub: PublicBuyerProfile;
  locale: string;
  t: (k: string, params?: Record<string, string | number>) => string;
}) {
  const { short: shortName, legalForm } = shortenOrgName(pub.name);
  // Rows the public tier can name at all — see `isNameableRow`. Computed once so
  // the section guard and the list agree; guarding on the raw array painted the
  // heading over an empty list when every row was a dash.
  const recentTenders = (pub.recentTenders ?? []).filter(isNameableRow);
  return (
    <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <p className={KICKER}>{t('buyer.title')}</p>
      <h1 className="mt-1 text-[20px] font-bold leading-tight text-ink [overflow-wrap:anywhere]">
        {shortName || pub.name || t('buyer.title')}
      </h1>
      {/* Registered legal form, demoted below the distinctive name it used to
          bury in the h1/title. Rendered only when stripping it changed the
          name. */}
      {legalForm && (
        <p className="mt-0.5 text-[12px] text-ink-muted [overflow-wrap:anywhere]">{pub.name}</p>
      )}
      <p className="mt-1 text-[13px] text-ink-muted">
        {t('buyer.tin')}: {pub.tin}
        {pub.region ? ` · ${localizeRegion(pub.region, locale) ?? pub.region}` : ''}
      </p>
      <GoodStandingBadge goodStanding={pub.goodStanding} kind="buyer" className="mt-2" />
      {/* No claim CTA here (2026-09-06): our users are suppliers, so nobody on a
          buyer profile is that buyer. The flow lives on /supplier only. */}

      {/* Soliq registry trust facts — public, same posture as the supplier
          `status`/`debarred` fields. No `regDate` here (see `PublicBuyerProfile`),
          so the «работает с YYYY» chip never renders on this page — expected,
          not a bug. `empty:hidden` drops the wrapper's own margin when
          BuyerTrustStrip renders nothing (every field empty), same pattern as
          the tender-detail documents section. */}
      <div className="mt-3 empty:hidden">
        <BuyerTrustStrip
          status={pub.status}
          vatPayer={pub.vatPayer}
          charterFundUzs={pub.charterFundUzs}
          liquidationDate={pub.liquidationDate}
          dxmapRateType={pub.dxmapRateType}
          dxmapSector={pub.dxmapSector}
          kgdFlags={pub.kgdFlags}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('buyer.kpi.awards')} value={String(pub.totalAwards)} />
        <StatTile
          label={t('buyer.kpi.value')}
          value={formatBudgetCompact(String(pub.totalValueUzs), locale) ?? '—'}
        />
        <StatTile label={t('buyer.kpi.suppliers')} value={String(pub.distinctSuppliers)} />
        <StatTile label={t('buyer.kpi.active')} value={String(pub.activeTenders)} />
      </div>

      {/* «Закупки за 12 месяцев» (2026-09-07) — the read a registry aggregator
          cannot show: how much this buyer awarded in the last year, the median
          discount and the share of single-bidder lots. Aggregates only; each
          figure carries its own coverage note (starting prices and participant
          counts exist for tender-mc and etender lots, not uzex-direct). */}
      {pub.procurement12m && (
        <div className="mt-6">
          <p className={`mb-2 ${KICKER}`}>{t('buyer.p12.title')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label={t('buyer.p12.awards')} value={String(pub.procurement12m.awards)} />
            <StatTile
              label={t('buyer.p12.value')}
              value={withCurrency(
                formatBudgetCompact(String(Math.round(pub.procurement12m.valueUzs)), locale) ?? '0',
                t('tender.budget.unit'),
              )}
            />
            {pub.procurement12m.medianDiscountPct != null && (
              <StatTile
                label={t('buyer.p12.discount')}
                value={pctStat(pub.procurement12m.medianDiscountPct, locale)}
                sub={t('buyer.p12.discountNote', { n: pub.procurement12m.pricedAwards })}
              />
            )}
            {pub.procurement12m.medianParticipants != null && (
              <StatTile
                label={t('buyer.p12.participants')}
                value={String(pub.procurement12m.medianParticipants)}
                sub={t('buyer.p12.participantsNote', { n: pub.procurement12m.countedAwards })}
              />
            )}
            {pub.procurement12m.singleBidderPct != null && (
              <StatTile
                label={t('buyer.p12.singleBidder')}
                value={pctStat(pub.procurement12m.singleBidderPct, locale)}
                sub={t('buyer.p12.participantsNote', { n: pub.procurement12m.countedAwards })}
              />
            )}
          </div>
          <p className="mt-2 text-[12px] text-ink-muted">{t('buyer.p12.source')}</p>
        </div>
      )}

      {pub.categoryMix.length > 0 && (
        <div className="mt-6">
          <p className={KICKER}>{t('buyer.categories')}</p>
          <RankedBars
            items={pub.categoryMix.map((c) => ({
              label: c.name ?? c.section,
              value: c.awards,
              valueText: String(c.awards),
            }))}
          />
        </div>
      )}

      {/* Recent tenders, PUBLIC (2026-09-07): what this buyer bought, for how
          much, when — each a link into the catalogue. The winner is not here;
          the named pairing stays on the Pro tier above. Crawlable on purpose:
          it is the evidence that makes the page worth indexing and worth a
          supplier's follow. Optional in the type — an API one deploy behind
          sends none and the block just does not render.
          ⚠️ The guard reads the FILTERED list, not the raw one: a payload whose
          every row is unnameable (see `isNameableRow`) would otherwise paint the
          heading over an empty `<ul>`. */}
      {recentTenders.length > 0 && (
        <div className="mt-6">
          <p className={`mb-2 ${KICKER}`}>{t('buyer.recentTenders')}</p>
          <ul className="divide-y divide-edge-light">
            {recentTenders.map((a, i) => (
              <li key={a.tenderId ?? i} className="py-2.5 first:pt-0">
                <div className="flex items-baseline justify-between gap-3">
                  {a.tenderId ? (
                    <Link
                      // The CANONICAL path. A bare `/tender/{id}` answers 200 and
                      // then self-canonicalises, so every one of these links cost
                      // a redirect and pointed a crawler at a duplicate.
                      to={tenderPath(a.slug, a.tenderId)}
                      className="min-w-0 truncate text-[14px] font-semibold text-brand hover:underline"
                      title={a.title != null ? displaySourceText(a.title) : a.tenderId}
                    >
                      {a.title != null ? displaySourceText(a.title) : a.tenderId}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate text-[14px] font-semibold text-ink">
                      {a.title && displaySourceText(a.title)}
                    </span>
                  )}
                  {/* Spain: an award under 1 € says so instead of leaving a gap
                      (review 21.10: «0 €» on /buyer/A84076397). */}
                  {amountShown(a.valueUzs) ? (
                    <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">
                      {withCurrency(
                        formatBudgetCompact(String(Math.round(a.valueUzs)), locale) ?? '0',
                        t('tender.budget.unit'),
                      )}
                    </span>
                  ) : (
                    <span className="shrink-0 text-[12.5px] text-ink-muted">
                      {t('buyer.amountUndisclosed')}
                    </span>
                  )}
                </div>
                {a.awardedAt && (
                  <div className="mt-0.5 text-[12px] text-ink-muted">
                    {formatDeadline(a.awardedAt, locale) ?? ''}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** One KPI cell in the buyer-header stat strip (on the brand-teal surface). */
function HeroKpi({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-[17px] font-extrabold leading-none tabular-nums text-white">{value}</div>
      <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.02em] text-white/55">
        {label}
      </div>
    </div>
  );
}
